import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { FaWhatsapp, FaSearch, FaUser, FaCircle, FaSpinner, FaPaperPlane } from 'react-icons/fa';
import Swal from 'sweetalert2';

const WhatsAppShareModal = ({ producto, onClose, token }) => {
    const [chats, setChats] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [loading, setLoading] = useState(true);
    const [sending, setSending] = useState(false);
    const [selectedChat, setSelectedChat] = useState(null);

    useEffect(() => {
        fetchChats();
        // eslint-disable-next-line
    }, []);

    const fetchChats = async (query = '') => {
        try {
            setLoading(true);
            const response = await axios.get(`/api/osobot/chats?limit=50&searchTerm=${query}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            if (response.data.status === 'success') {
                setChats(response.data.chats);
            }
        } catch (error) {
            console.error('Error fetching chats:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleSearch = (e) => {
        const val = e.target.value;
        setSearchTerm(val);
        // Debounce simple
        const timeoutId = setTimeout(() => fetchChats(val), 500);
        return () => clearTimeout(timeoutId);
    };

    const handleSend = async () => {
        if (!selectedChat || !producto) return;

        try {
            setSending(true);
            const response = await axios.post('/api/osobot/send-product-from-operator', {
                conversationId: selectedChat.id,
                product: producto
            }, {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (response.data.status === 'success') {
                Swal.fire({
                    icon: 'success',
                    title: 'Enviado',
                    text: `Información de ${producto.nombre_producto} enviada a ${selectedChat.name}`,
                    timer: 2000,
                    showConfirmButton: false
                });
                onClose();
            } else {
                throw new Error(response.data.message);
            }
        } catch (error) {
            console.error('Error sending product:', error);
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: 'No se pudo enviar la información del producto.'
            });
        } finally {
            setSending(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[100000] p-4">
            <div className="bg-white rounded-3xl shadow-2xl w-full max-w-md overflow-hidden flex flex-col max-h-[90vh] animate-in fade-in zoom-in duration-300">
                
                {/* Header */}
                <div className="bg-green-600 p-6 text-white relative">
                    <div className="flex items-center gap-3">
                        <div className="bg-white/20 p-3 rounded-2xl">
                            <FaWhatsapp size={24} />
                        </div>
                        <div>
                            <h3 className="text-xl font-bold leading-tight">Compartir Producto</h3>
                            <p className="text-green-100 text-xs">Selecciona un chat activo</p>
                        </div>
                    </div>
                    <button 
                        onClick={onClose}
                        className="absolute top-6 right-6 text-white/80 hover:text-white text-2xl"
                    >
                        &times;
                    </button>
                </div>

                {/* Product Preview Short */}
                <div className="p-4 bg-gray-50 border-b flex items-center gap-3">
                    <div className="w-12 h-12 rounded-lg bg-white border border-gray-200 overflow-hidden flex-shrink-0">
                        <img 
                            src={producto.url || producto.imagenes?.[0]?.url} 
                            alt="" 
                            className="w-full h-full object-contain"
                        />
                    </div>
                    <div className="overflow-hidden">
                        <h4 className="text-sm font-bold text-gray-800 truncate">{producto.nombre_producto}</h4>
                        <p className="text-[10px] text-gray-500 font-mono italic">SKU: {producto.sku}</p>
                    </div>
                </div>

                {/* Search */}
                <div className="p-4 border-b">
                    <div className="relative">
                        <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={14} />
                        <input 
                            type="text"
                            placeholder="Buscar contacto o teléfono..."
                            className="w-full pl-10 pr-4 py-2.5 bg-gray-100 border-none rounded-xl focus:ring-2 focus:ring-green-500 text-sm outline-none transition-all"
                            value={searchTerm}
                            onChange={handleSearch}
                        />
                    </div>
                </div>

                {/* Chat List */}
                <div className="flex-grow overflow-y-auto p-2 space-y-1 min-h-[300px]">
                    {loading ? (
                        <div className="flex flex-col items-center justify-center h-full py-10 text-gray-400">
                            <FaSpinner className="animate-spin mb-2" size={24} />
                            <p className="text-sm">Cargando chats...</p>
                        </div>
                    ) : chats.length === 0 ? (
                        <div className="text-center py-10 text-gray-400">
                            <p className="text-sm font-medium">No se encontraron chats activos</p>
                        </div>
                    ) : (
                        chats.map(chat => (
                            <button
                                key={chat.id}
                                onClick={() => setSelectedChat(chat)}
                                className={`w-full flex items-center gap-3 p-3 rounded-2xl transition-all ${selectedChat?.id === chat.id ? 'bg-green-50 border border-green-100 shadow-sm' : 'hover:bg-gray-50 border border-transparent'}`}
                            >
                                <div className="relative mt-1">
                                    <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center text-gray-500">
                                        <FaUser size={18} />
                                    </div>
                                    <div className="absolute -bottom-0.5 -right-0.5">
                                        <FaCircle className={chat.status === 'bot' ? 'text-blue-500' : 'text-green-500'} size={12} strokeWidth={2} stroke="white" />
                                    </div>
                                </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center justify-between mb-0.5">
                                            <h3 className="text-sm font-bold text-gray-800 truncate">{chat.name}</h3>
                                            <span className="text-[10px] text-gray-400 font-medium">{chat.lastMessageDate}</span>
                                        </div>
                                        <p className="text-xs text-gray-500 truncate mb-1">
                                            {(() => {
                                                const msg = chat.lastMessage;
                                                if (!msg) return 'Sin mensajes';
                                                if (typeof msg === 'string') return msg;
                                                if (typeof msg !== 'object') return 'Mensaje';

                                                // msg.body puede ser string O un objeto {mediaId, caption}
                                                const body = msg.body;
                                                if (typeof body === 'string' && body.length > 0) return body;
                                                if (typeof body === 'object' && body !== null) {
                                                    if (body.caption) return body.caption;
                                                    if (body.mediaId) return '📷 Imagen';
                                                    if (body.text) return body.text;
                                                }

                                                // Fallbacks por tipo de mensaje
                                                if (typeof msg.text === 'string') return msg.text;
                                                if (typeof msg.caption === 'string') return msg.caption;
                                                switch (msg.type) {
                                                    case 'image': return '📷 Imagen';
                                                    case 'audio': return '🎙️ Audio';
                                                    case 'document': return '📄 Documento';
                                                    case 'sticker': return '🏷️ Sticker';
                                                    case 'template': return '📋 Plantilla';
                                                    case 'video': return '🎬 Video';
                                                    default: return 'Mensaje';
                                                }
                                            })()}
                                        </p>
                                        
                                        {/* Metadatos de Contexto (Vehículo/Producto) */}
                                        <div className="flex flex-wrap gap-1">
                                            {chat.context?.vehicle && (
                                                <span className="inline-flex items-center px-1.5 py-0.5 rounded-lg bg-blue-50 text-blue-600 text-[8px] font-black uppercase tracking-tighter border border-blue-100">
                                                    🚗 {String(typeof chat.context.vehicle === 'object' ? (chat.context.vehicle?.name || chat.context.vehicle?.marca || JSON.stringify(chat.context.vehicle)) : chat.context.vehicle).substring(0, 15)}
                                                </span>
                                            )}
                                            {chat.context?.product && (
                                                <span className="inline-flex items-center px-1.5 py-0.5 rounded-lg bg-cyan-50 text-cyan-600 text-[8px] font-bold uppercase tracking-tighter border border-cyan-100">
                                                    🔧 {String(typeof chat.context.product === 'object' ? (chat.context.product?.nombre_producto || chat.context.product?.name || JSON.stringify(chat.context.product)) : chat.context.product).substring(0, 15)}
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                {selectedChat?.id === chat.id && (
                                    <div className="ml-auto bg-green-500 w-2 h-2 rounded-full mt-4"></div>
                                )}
                            </button>
                        ))
                    )}
                </div>

                {/* Footer Actions */}
                <div className="p-4 border-t bg-gray-50">
                    <button
                        onClick={handleSend}
                        disabled={!selectedChat || sending}
                        className={`w-full py-3 rounded-2xl font-bold flex items-center justify-center gap-2 transition-all ${!selectedChat || sending ? 'bg-gray-300 text-gray-500 cursor-not-allowed' : 'bg-green-600 text-white shadow-lg shadow-green-200 hover:bg-green-700 active:scale-95'}`}
                    >
                        {sending ? (
                            <>
                                <FaSpinner className="animate-spin" />
                                <span>Enviando...</span>
                            </>
                        ) : (
                            <>
                                <FaPaperPlane size={14} />
                                <span>Enviar a {selectedChat ? selectedChat.name.split(' ')[0] : '...'}</span>
                            </>
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default WhatsAppShareModal;
